import React, { useEffect, useState } from 'react';
import { getAssayWorksheetKeyAudit, apiErrorMessage, type AssayWorksheetKeyAudit } from '../../api/limsClient';
import { formatDate } from '../../utils/formatDate';
import './AssayDefinition.css';

interface Props {
  assayId: number;
  /** Re-fetch when the definition changes, so newly declared keys drop off the list. */
  definitionVersion: number;
}

/** Undeclared worksheet keys seen on this assay's tests: the gap between what is written and what is declared. */
export const AssayAuditPanel: React.FC<Props> = ({ assayId, definitionVersion }) => {
  const [audit, setAudit] = useState<AssayWorksheetKeyAudit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeclared, setShowDeclared] = useState(false);

  const load = (refresh: boolean) => {
    setLoading(true);
    setError(null);
    getAssayWorksheetKeyAudit(assayId, refresh)
      .then(setAudit)
      .catch(err => setError(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(definitionVersion > 0); }, [assayId, definitionVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const undeclared = audit?.keys.filter(k => !k.declared) ?? [];
  const declared = audit?.keys.filter(k => k.declared) ?? [];

  return (
    <div className="assay-panel">
      <h3 className="assay-panel-heading">
        <span>Worksheet key audit</span>
        <button type="button" className="assay-btn small" disabled={loading} onClick={() => load(true)}>{loading ? 'Loading…' : 'Refresh'}</button>
      </h3>
      {error && <div className="assay-error">{error}</div>}
      {!error && audit && (
        <>
          <div className="assay-muted" style={{ marginBottom: '0.375rem' }}>
            {audit.testsWithWorksheet} test{audit.testsWithWorksheet === 1 ? '' : 's'} with worksheet data
            {!audit.hasDefinition && ' · no definition yet, so every key is undeclared'}
            {audit.generatedAt && <> · as of {formatDate(audit.generatedAt)}</>}
          </div>
          {undeclared.length === 0 ? (
            <div className="assay-success">No undeclared keys seen.</div>
          ) : (
            <table className="audit-table">
              <thead>
                <tr><th>Undeclared key</th><th>Tests</th><th>Last seen</th><th>Units</th></tr>
              </thead>
              <tbody>
                {undeclared.map(k => (
                  <tr key={k.key}>
                    <td><code title={k.exampleValue ? `e.g. ${k.exampleValue}` : undefined}>{k.key}</code></td>
                    <td>{k.tests}</td>
                    <td>{k.lastSeen}</td>
                    <td>{k.units.join(', ') || <span className="assay-muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {declared.length > 0 && (
            <div style={{ marginTop: '0.375rem' }}>
              <button type="button" className="json-toggle-btn" onClick={() => setShowDeclared(!showDeclared)}>
                {showDeclared ? 'Hide' : 'Show'} {declared.length} declared key{declared.length === 1 ? '' : 's'} seen
              </button>
              {showDeclared && (
                <table className="audit-table" style={{ marginTop: '0.25rem' }}>
                  <thead><tr><th>Key</th><th>Tests</th><th>Last seen</th></tr></thead>
                  <tbody>
                    {declared.map(k => (
                      <tr key={k.key}><td><code>{k.key}</code></td><td>{k.tests}</td><td>{k.lastSeen}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
      {!error && !audit && loading && <div className="assay-muted">Scanning worksheet data…</div>}
    </div>
  );
};
